import { useState, useEffect, useMemo } from 'react';
import { useDebouncedCallback } from 'use-debounce';
import {
  Table,
  Button,
  Input,
  Select,
  DatePicker,
  message,
  Tag,
  Tooltip,
  Descriptions,
  Modal,
} from 'antd';
import {
  SearchOutlined,
  ReloadOutlined,
  EyeOutlined,
} from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import { useFetchCronList } from '../../Hooks/Index';

dayjs.extend(utc);
dayjs.extend(timezone);

const CronListPage = () => {
  const { t } = useTranslation('cron-list');
  
  // State for pagination
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 20,
  });
  
  // State for filters
  const [filters, setFilters] = useState({
    search: '',
    status: 'COMPLETED',
    hasError: undefined,
    name: null,
    type: null,
    dateRange: [dayjs().subtract(30, 'days'), dayjs()],
  });
  const [filtersApplied, setFiltersApplied] = useState(false);

  // Memoize query parameters
  const queryParams = useMemo(() => {
    const params = {
      status: filters.status,
      limit: pagination.limit,
      offset: pagination.page,
    };
    
    if (filters.dateRange?.[0] && filters.dateRange?.[1]) {
      params.startDate = filters.dateRange[0].format('YYYY-MM-DD');
      params.endDate = filters.dateRange[1].format('YYYY-MM-DD');
    }
    
    if (filters.hasError !== undefined) {
      params.hasError = filters.hasError;
    }
    
    if (filters.name) {
      params.name = filters.name;
    }
    
    if (filters.type) {
      params.type = filters.type;
    }
    
    return params;
  }, [pagination, filters]);

  // Fetch cron list data
  const {
    data: cronData,
    isLoading,
    isFetching,
    error,
    refetch,
  } = useFetchCronList(queryParams);

  // Status options - memoized to recalculate when translations change
  const statusOptions = useMemo(() => [
    { value: 'COMPLETED', label: t('completed'), color: 'green' },
    { value: 'PENDING', label: t('pending'), color: 'orange' },
    { value: 'RUNNING', label: t('running'), color: 'blue' },
    { value: 'FAILED', label: t('failed'), color: 'red' },
  ], [t]);

  // Get status color helper
  const getStatusColor = (status) => {
    const statusOption = statusOptions.find(opt => opt.value === status);
    return statusOption?.color || 'default';
  };

  // Handle table change (sorting, filtering)
  const handleTableChange = (paginationInfo) => {
    setPagination(prev => ({
      ...prev,
      page: paginationInfo.current,
      limit: paginationInfo.pageSize,
    }));
  };

  // Handle filter changes
  const handleFilterChange = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
    setPagination(prev => ({ ...prev, page: 1 }));
  };

  // Handle search with debouncing
  const handleSearchChange = useDebouncedCallback((value) => {
    handleFilterChange('search', value);
  }, 300);

  const handleResetFilters = () => {
    setFilters({
      search: '',
      status: 'COMPLETED',
      hasError: undefined,
      name: null,
      type: null,
      dateRange: [dayjs().subtract(30, 'days'), dayjs()],
    });
    setFiltersApplied(false);
    setPagination(prev => ({ ...prev, page: 1 }));
  };

  // Check if filters are applied
  useEffect(() => {
    setFiltersApplied(
      !!(filters.search || filters.hasError !== undefined || 
         (filters.dateRange && filters.dateRange.length === 2 && 
          (filters.dateRange[0] || filters.dateRange[1])))
    );
  }, [filters]);

  // Handle data fetching errors
  useEffect(() => {
    if (error) {
      const errorMessage = error?.response?.data?.message || 
                          error?.response?.data?.status_message ||
                          'Failed to load cron list data';
      message.error(errorMessage);
      console.error('Cron list fetch error:', error);
    }
  }, [error]);

  // View details modal state
  const [viewModalVisible, setViewModalVisible] = useState(false);
  const [selectedRecord, setSelectedRecord] = useState(null);

  const handleViewDetails = (record) => {
    setSelectedRecord(record);
    setViewModalVisible(true);
  };

  // Helper to convert Unix timestamp to date
  const formatUnixTimestamp = (timestamp) => {
    if (!timestamp) return 'N/A';
    // Convert Unix timestamp (seconds) to milliseconds
    const date = dayjs.unix(Number(timestamp));
    return date.format('YYYY-MM-DD HH:mm:ss');
  };

  // Helper to check if record has errors
  const hasError = (record) => {
    return record.status === 'FAILED' || (record.data?.failedCount > 0);
  };

  // Table columns
  const columns = [
    {
      title: t('name'),
      dataIndex: 'name',
      key: 'name',
      width: 200,
      ellipsis: true,
      align: 'center',
      render: (text, record) => (
        <span 
          title={text} 
          className="text-sm cursor-pointer hover:text-blue-600 hover:underline"
          onClick={() => handleViewDetails(record)}
        >
          {text || 'N/A'}
        </span>
      ),
    },
    {
      title: t('type'),
      dataIndex: 'type',
      key: 'type',
      width: 150,
      align: 'center',
      render: (text) => text || 'N/A',
    },
    {
      title: t('status'),
      dataIndex: 'status',
      key: 'status',
      width: 120,
      align: 'center',
      render: (status) => (
        <Tag color={getStatusColor(status)} className="capitalize font-medium" variant="outlined">
          {status || 'N/A'}
        </Tag>
      ),
    },
    {
      title: t('hasError'),
      key: 'hasError',
      width: 100,
      align: 'center',
      render: (_, record) => {
        const error = hasError(record);
        return (
          <Tag color={error ? 'red' : 'green'} variant="outlined">
            {error ? t('yes') : t('no')}
          </Tag>
        );
      },
    },
    {
      title: t('processed'),
      dataIndex: ['data', 'processedCount'],
      key: 'processedCount',
      width: 100,
      align: 'center',
      render: (count) => count ?? 0,
    },
    {
      title: t('success'),
      dataIndex: ['data', 'successCount'],
      key: 'successCount',
      width: 100,
      align: 'center',
      render: (count) => (
        <Tag color="green" variant="outlined">
          {count ?? 0}
        </Tag>
      ),
    },
    {
      title: t('failed'),
      dataIndex: ['data', 'failedCount'],
      key: 'failedCount',
      width: 100,
      align: 'center',
      render: (count) => (
        <Tag color={count > 0 ? 'red' : 'default'} variant="outlined">
          {count ?? 0}
        </Tag>
      ),
    },
    {
      title: t('skipped'),
      dataIndex: ['data', 'skippedCount'],
      key: 'skippedCount',
      width: 100,
      align: 'center',
      render: (count) => count ?? 0,
    },
    {
      title: t('createdAt'),
      dataIndex: 'created_at',
      key: 'created_at',
      width: 180,
      align: 'center',
      render: (timestamp) => (
        <span className="text-sm">
          {formatUnixTimestamp(timestamp)}
        </span>
      ),
      sorter: (a, b) => Number(a.created_at) - Number(b.created_at),
    },
    {
      title: t('updatedAt'),
      dataIndex: 'updated_at',
      key: 'updated_at',
      width: 180,
      align: 'center',
      render: (timestamp) => (
        <span className="text-sm">
          {formatUnixTimestamp(timestamp)}
        </span>
      ),
      sorter: (a, b) => Number(a.updated_at) - Number(b.updated_at),
    },
    {
      title: t('actions'),
      key: 'actions',
      width: 80,
      align: 'center',
      render: (_, record) => (
        <div className="flex gap-2 items-center justify-center">
          <Tooltip title={t('viewDetails')}>
            <Button
              type="default"
              icon={<EyeOutlined />}
              onClick={() => handleViewDetails(record)}
            />
          </Tooltip>
        </div>
      ),
    },
  ];

  // Extract data from response
  const tableData = cronData?.paginatedData || [];
  const totalCount = cronData?.totalCount || 0;

  return (
    <div>
      <div className="flex flex-col md:flex-row gap-4">
        <div className='flex-1 flex flex-row gap-4'>
          <div className="">
            <Input
              placeholder={t('searchPlaceholder')}
              prefix={<SearchOutlined />}
              value={filters.search}
              onChange={(e) => {
                setFilters(prev => ({ ...prev, search: e.target.value }));
                handleSearchChange(e.target.value);
              }}
              className="w-full md:!min-w-[200px]"
              allowClear
            />
          </div>
          
          <div className="">
            <Select
              placeholder={t('selectStatus')}
              value={filters.status}
              onChange={(value) => handleFilterChange('status', value)}
              allowClear
              className="w-full md:!min-w-[150px]"
              options={statusOptions}
            />
          </div>

          <div className="">
            <Select
              placeholder={t('hasErrorPlaceholder')}
              value={filters.hasError}
              onChange={(value) => handleFilterChange('hasError', value)}
              allowClear
              className="w-full md:!min-w-[120px]"
            >
              <Select.Option value={false}>{t('no')}</Select.Option>
              <Select.Option value={true}>{t('yes')}</Select.Option>
            </Select>
          </div>

          <div>
            <DatePicker.RangePicker
              value={filters.dateRange}
              onChange={(value) => handleFilterChange('dateRange', value)}
              format="YYYY-MM-DD"
              className="w-full md:w-[240px]"
              presets={[
                { label: t('presets.today'), value: [dayjs(), dayjs()] },
                { label: t('presets.yesterday'), value: [dayjs().add(-1, 'd'), dayjs().add(-1, 'd')] },
                { label: t('presets.last7Days'), value: [dayjs().add(-7, 'd'), dayjs()] },
                { label: t('presets.last30Days'), value: [dayjs().add(-30, 'd'), dayjs()] },
                { label: t('presets.thisMonth'), value: [dayjs().startOf('month'), dayjs().endOf('month')] },
              ]}
            />
          </div>

          {filtersApplied && (
            <Button onClick={handleResetFilters}>
              {t('resetFilters')}
            </Button>
          )}
          <Button
            icon={<ReloadOutlined />}
            loading={isFetching}
            type="default"
            onClick={() => refetch()}
          >
            {t('refresh')}
          </Button>
        </div>
      </div>

      <div className='w-full mt-4'>
        <Table
          columns={columns}
          dataSource={tableData}
          rowKey={(record, index) => record._id || record.id || `cron-${index}`}
          loading={isLoading || isFetching}
          pagination={{
            current: pagination.page,
            showSizeChanger: true,
            total: totalCount,
            pageSize: pagination.limit,
            pageSizeOptions: ['10', '20', '50', '100'],
            onChange: (page, size) => {
              setPagination(prev => ({ ...prev, page: page, limit: size }));
            },
            onShowSizeChange: (current, size) => {
              setPagination(prev => ({ ...prev, page: 1, limit: size }));
            },
            size: 'default',
            position: ['bottomCenter'],
          }}
          onChange={handleTableChange}
          size="middle"
          scroll={{ x: 1600, y: '67vh' }}
          variant="outlined"
          locale={{
            emptyText: t('noCronJobsFound'),
          }}
        />
      </div>

      {/* View Details Modal */}
      <Modal
        title={
          <div className="flex items-center gap-2">
            <EyeOutlined />
            <span>{t('cronJobDetails')}</span>
            {selectedRecord && (
              <Tag color={getStatusColor(selectedRecord.status)} className="ml-2" variant="outlined">
                {selectedRecord.status || 'N/A'}
              </Tag>
            )}
          </div>
        }
        open={viewModalVisible}
        centered={true}
        onCancel={() => {
          setViewModalVisible(false);
          setSelectedRecord(null);
        }}
        width={900}
        footer={[
          <Button key="close" onClick={() => {
            setViewModalVisible(false);
            setSelectedRecord(null);
          }}>
            {t('close')}
          </Button>,
        ]}
        className="cron-list-modal"
      >
        {selectedRecord && (
          <Descriptions bordered column={1}>
            <Descriptions.Item label={t('name')}>
              {selectedRecord.name || 'N/A'}
            </Descriptions.Item>
            <Descriptions.Item label={t('type')}>
              {selectedRecord.type || 'N/A'}
            </Descriptions.Item>
            <Descriptions.Item label={t('status')}>
              <Tag color={getStatusColor(selectedRecord.status)} variant="outlined">
                {selectedRecord.status || 'N/A'}
              </Tag>
            </Descriptions.Item>
            <Descriptions.Item label={t('hasError')}>
              <Tag color={hasError(selectedRecord) ? 'red' : 'green'} variant="outlined">
                {hasError(selectedRecord) ? t('yes') : t('no')}
              </Tag>
            </Descriptions.Item>
            {selectedRecord.data && (
              <>
                <Descriptions.Item label={t('processedCount')}>
                  {selectedRecord.data.processedCount ?? 0}
                </Descriptions.Item>
                <Descriptions.Item label={t('successCount')}>
                  <Tag color="green" variant="outlined">
                    {selectedRecord.data.successCount ?? 0}
                  </Tag>
                </Descriptions.Item>
                <Descriptions.Item label={t('failedCount')}>
                  <Tag color={selectedRecord.data.failedCount > 0 ? 'red' : 'default'} variant="outlined">
                    {selectedRecord.data.failedCount ?? 0}
                  </Tag>
                </Descriptions.Item>
                <Descriptions.Item label={t('skippedCount')}>
                  {selectedRecord.data.skippedCount ?? 0}
                </Descriptions.Item>
              </>
            )}
            <Descriptions.Item label={t('createdAt')}>
              {formatUnixTimestamp(selectedRecord.created_at)}
            </Descriptions.Item>
            <Descriptions.Item label={t('updatedAt')}>
              {formatUnixTimestamp(selectedRecord.updated_at)}
            </Descriptions.Item>
            {selectedRecord.logs && (
              <Descriptions.Item label={t('logs')}>
                <pre className="bg-gray-50 p-2 rounded text-xs overflow-auto max-h-60">
                  {(() => {
                    try {
                      const logsArray = JSON.parse(selectedRecord.logs);
                      return JSON.stringify(logsArray, null, 2);
                    } catch {
                      return selectedRecord.logs;
                    }
                  })()}
                </pre>
              </Descriptions.Item>
            )}
          </Descriptions>
        )}
      </Modal>
    </div>
  );
};

export default CronListPage;

